import React, { useCallback, useMemo, useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import { ResizeObserver } from "@juggle/resize-observer";
import useMeasure from "react-use-measure";

import { Legend } from "./Legend";
import { useStore } from "../../store";
import { makeNetworkFromNarrative } from "./hierarchy";
import narrative from "./narratives";
import { CHART_LAYOUT } from "./render";
import { defaultTooltip, Controls } from "./Controls";
import { HierarchicalGraph } from "./HierarchicalGraph";
import "./CorporateSnapshots.scss";

export const CorporateSnapshots = ({
  className = "",
  enableScroll = true,
  showChart = false,
}) => {
  const dispatch = useStore(useCallback((state) => state.dispatch, []));
  const narrativeIdx = useStore(
    useCallback((state) => state.app.corporate.narrativeIdx, [])
  );
  const showNameTooltipsForAllCompanies = useStore(
    useCallback(
      (state) => state.app.corporate.showNameTooltipsForAllCompanies,
      []
    )
  );
  const [tooltip, setTooltip] = useState(defaultTooltip);
  const [chartOrientation, setChartOrientation] = useState(
    CHART_LAYOUT.HORIZONTAL
  );

  const onTooltipSelect = useCallback((t) => {
    setTooltip(t);
  }, []);

  const [svgContainerRef, bounds] = useMeasure({ polyfill: ResizeObserver });

  useEffect(() => {
    if (bounds.width > bounds.height) {
      chartOrientation === CHART_LAYOUT.VERTICAL &&
        setChartOrientation(CHART_LAYOUT.HORIZONTAL);
    } else {
      chartOrientation === CHART_LAYOUT.HORIZONTAL &&
        setChartOrientation(CHART_LAYOUT.VERTICAL);
    }
  }, [bounds, chartOrientation]);

  const { root, partialEdges, highlightedCompanies } = useMemo(() => {
    const { data, highlightedCompanies } = narrative[narrativeIdx || 0];
    const { root, partialEdges } = makeNetworkFromNarrative(
      data,
      chartOrientation
    );
    return {
      root,
      partialEdges,
      highlightedCompanies,
    };
  }, [narrativeIdx, chartOrientation]);

  // This callback is not updated in D3 when partialEdges change.
  const onNodeMouseEnter = useCallback(
    ({
      company,
      incorporated,
      notes,
      country,
      curatedText,
      showCuratedTextAfterChapter,
      x,
      y,
    }) => {
      console.log(partialEdges);
      onTooltipSelect({
        edges: partialEdges.filter((d) => d.target === company),
        country: country,
        incorporated: incorporated,
        company,
        text: showCuratedTextAfterChapter <= narrativeIdx ? curatedText : null,
        notes: notes,
        x,
        y,
      });
    },
    [onTooltipSelect, partialEdges, narrativeIdx]
  );

  const onNodeMouseOut = useCallback(() => {
    onTooltipSelect(defaultTooltip);
  }, [onTooltipSelect]);

  return (
    <>
      <div className={`corporate-snapshots ${className}`}>
        <>
          {/* <div className="full-height no-pointer-events"></div> */}
          {enableScroll ? (
            <div className="narrative">
              {narrative.map((step, stepIdx) => (
                <InView
                  key={`explore-corporate-narrative-${stepIdx}`}
                  as="section"
                  threshold={0.5}
                  // initialInView={false}
                  onChange={(inView) => {
                    inView &&
                      dispatch({
                        type: `CORPORATE_UPDATE_NARRATIVE_IDX`,
                        payload: stepIdx,
                      });
                  }}
                >
                  <div
                    className="narrative__scene-container pointer-events  block-font h2"
                    style={{ height: `100vh` }}
                  >
                    <div className="narrative__scene-content center">
                      <div className="narrative__scene-metadata">
                        <div className="bold">
                          {narrative[stepIdx]?.title || ""}
                        </div>
                        <div className="italic color-gray">
                          {narrative[stepIdx]?.date || ""}
                        </div>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: narrative[stepIdx]?.text || "",
                        }}
                      ></div>
                    </div>
                  </div>
                </InView>
              ))}
            </div>
          ) : (
            <InView
              as="section"
              threshold={1}
              initialInView={false}
              className="full-height"
              style={{ height: `300vh` }}
              onChange={(inView) => {
                inView &&
                  dispatch({
                    type: `CORPORATE_UPDATE_NARRATIVE_IDX`,
                    payload: 0,
                  });
              }}
            />
          )}
        </>

        {/* <div className="navbar flex col-12"></div> */}
      </div>
      {showChart && (
        <div className="corporate-chart fade-in" ref={svgContainerRef}>
          <Controls tooltip={tooltip} />
          <Legend />
          <HierarchicalGraph
            root={root}
            partialEdges={partialEdges}
            bounds={bounds}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseOut={onNodeMouseOut}
            onTooltipSelect={onTooltipSelect}
            chartOrientation={chartOrientation}
            highlightedCompanies={highlightedCompanies}
            showNameTooltipsForAllCompanies={showNameTooltipsForAllCompanies}
          />
        </div>
      )}
    </>
  );
};
